import { Component, OnInit, OnDestroy } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, GridOptions, ApplyColumnStateParams } from 'ag-grid-community';
import { AgGridOptionsBuilder, AgGridFilterParams, AgGridColumns } from '../../../Compliance/AgGrid';
import { TaxAccessSyncLogListAgGridDataSource } from './agGridDataSource';
import { WeissmanTimestampMsecFormatPipe } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { Subscription } from 'rxjs';
import { TaxAccessSyncLogService } from '../taxAccessSyncLog.service';
import { TaxAccessSyncLogRepository } from '../taxAccessSyncLog.repository';
import { TaxAccessSyncLogListGridActionCellRendererComponent, TaxAccessSyncLogListGridActionCellRendererParams } from './agGridActionCellRenderer.component';
import { WeissmanModalService } from '../../../Compliance/WeissmanModalService';
import { TaxAccessSyncLogDetailsModalComponent, TaxAccessSyncLogDetailsParams } from '../Tax-Access-Sync-Log-Details/taxAccessSyncLogDetailsModal.component';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import { RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';

@Component({
    selector: 'tax-access-sync-log-list',
    templateUrl: './taxAccessSyncLogList.component.html',
    styleUrls: ['./taxAccessSyncLogList.component.scss'],
    standalone: false
})
export class TaxAccessSyncLogListComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _timestampPipe: WeissmanTimestampMsecFormatPipe,
        private readonly _taxAccessSyncLogRepository: TaxAccessSyncLogRepository,
        private readonly _taxAccessSyncLogService: TaxAccessSyncLogService,
        private readonly _modalService: WeissmanModalService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _restrictService: RestrictService,
        private readonly _routerService: UpgradeNavigationServiceHandler) {
    }

    gridId: System.Guid = '8C6A6C0B-6E6E-4A3B-9A3B-7B3C6E1D6F2A';
    isInitialized: boolean = false;
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withColumnPinning()
        .buildDefault(this);
    gridApi: GridApi;

    private readonly _runTypeLabels: { [key: number]: string } = {
        [Core.TaxAccessSyncRunTypeEnum.Nightly]: 'Nightly',
        [Core.TaxAccessSyncRunTypeEnum.Baseline]: 'Baseline'
    };

    private readonly _runStatusLabels: { [key: number]: string } = {
        [Core.TaxAccessSyncRunStatusEnum.Running]: 'Running',
        [Core.TaxAccessSyncRunStatusEnum.Succeeded]: 'Succeeded',
        [Core.TaxAccessSyncRunStatusEnum.PartiallyFailed]: 'Partially Failed',
        [Core.TaxAccessSyncRunStatusEnum.Failed]: 'Failed'
    };

    private _gridDataSource: TaxAccessSyncLogListAgGridDataSource;
    private _refreshSub: Subscription;

    get refreshing(): boolean {
        return this._gridDataSource && this._gridDataSource.isRefreshing;
    }

    ngOnInit(): void {
        if (!(this._restrictService.isInRole(Roles.ADMINOPERATIONSVIEW) || this._restrictService.isInRole(Roles.ADMINOPERATIONSEDIT))) {
            this.navigateToUnauthorized();
            return;
        }

        this._refreshSub = this._taxAccessSyncLogService.refresh$.subscribe(() => {
            this._refreshDataSource();
        });

        this.isInitialized = true;
    }

    ngOnDestroy(): void {
        this._refreshSub && this._refreshSub.unsubscribe();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Summary ID',
                field: 'taxAccessSyncSummaryLogId',
                width: AgGridColumns.textColumnSmallWidth,
                maxWidth: AgGridColumns.textColumnMedWidth
            },
            {
                headerName: 'Batch ID',
                field: 'batchId',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Run Type',
                field: 'runType',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxAccessSyncSummaryLogModel;
                    if (!model) {
                        return '';
                    }

                    return this._runTypeLabels[model.runType] || '';
                }
            },
            {
                headerName: 'Run Status',
                field: 'runStatus',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxAccessSyncSummaryLogModel;
                    if (!model) {
                        return '';
                    }

                    return this._runStatusLabels[model.runStatus] || '';
                }
            },
            {
                headerName: 'Start Time',
                field: 'startTimeUtc',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxAccessSyncSummaryLogModel;
                    if (!(model && model.startTimeUtc)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.startTimeUtc);
                }
            },
            {
                headerName: 'End Time',
                field: 'endTimeUtc',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxAccessSyncSummaryLogModel;
                    if (!(model && model.endTimeUtc)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.endTimeUtc);
                }
            },
            {
                headerName: 'Processed',
                field: 'processedCount',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Sent',
                field: 'sentCount',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Skipped',
                field: 'skippedCount',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Failed',
                field: 'failedCount',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Unmapped',
                field: 'unmappedCount',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(1),
                minWidth: AgGridColumns.getActionColumnWidth(1),
                maxWidth: AgGridColumns.getActionColumnWidth(1),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                cellRenderer: TaxAccessSyncLogListGridActionCellRendererComponent,
                cellRendererParams: {
                    viewDetails: this._viewDetails.bind(this)
                } as TaxAccessSyncLogListGridActionCellRendererParams
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'taxAccessSyncSummaryLogId', sort: 'desc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('paginationPageSize', 100);
        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this._setDataSource();
    }

    async refresh(): Promise<void> {
        const busyRef = this._busyService.show({ message: 'Loading' });

        try {
            await this._taxAccessSyncLogService.refresh();
        } finally {
            busyRef.hide();
        }
    }

    navigateToUnauthorized(): void {
        this._routerService.go('unauthorizedAccess', {});
    }

    private async _viewDetails(params: TaxAccessSyncLogListGridActionCellRendererParams): Promise<void> {
        const taxAccessSyncSummaryLog = params.data as Core.TaxAccessSyncSummaryLogModel;
        if (!taxAccessSyncSummaryLog) {
            return;
        }

        const detailParams: TaxAccessSyncLogDetailsParams = {
            summaryLogId: taxAccessSyncSummaryLog.taxAccessSyncSummaryLogId
        };

        await this._modalService.showAsync(TaxAccessSyncLogDetailsModalComponent, detailParams, 'modal-lg');
    }

    private _refreshDataSource(): void {
        if (!this._gridDataSource) {
            const success = this._setDataSource();
            if (!success) {
                return;
            }
        }

        this._gridDataSource.refresh();
    }

    private _setDataSource(): boolean {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        this._gridDataSource = new TaxAccessSyncLogListAgGridDataSource(this.gridApi, this._taxAccessSyncLogRepository);
        this.gridApi.updateGridOptions({ datasource: this._gridDataSource });
        return true;
    }
}
